import React, { useState } from 'react';
import { X, RefreshCw, CheckCircle, AlertCircle, Info, ChevronDown, ChevronUp, Package, Download } from 'lucide-react';
import { itemService } from '../services/itemService';
import { useStore } from '../utils/StoreContext';
import { useAuth } from '../utils/AuthContext';
import { supabase } from '../utils/supabase';
import LoadingSpinner from './LoadingSpinner';
import * as XLSX from 'xlsx';

interface BulkPriceSyncModalProps {
  onClose: () => void;
  onSyncComplete?: () => void;
}

interface StockSnapshotItem {
  item_id: string;
  sku: string;
  item_name: string;
  pos_stock: number;
  pos_price: number;
  stock_room_stock: number;
  stock_room_price: number;
}

interface SyncReport {
  success: boolean;
  dry_run?: boolean;
  total_pos_items: number;
  total_stockroom_items: number;
  matched_by_sku: number;
  price_differences: number;
  updated: number;
  skipped: number;
  errors: Array<{ sku: string; error: string }>;
  changes: Array<{
    sku: string;
    item_name?: string;
    old_price: number | null;
    new_price: number;
    old_description?: string | null;
    new_description?: string;
    old_barcode?: string | null;
    new_barcode?: string;
    old_name?: string | null;
    new_name?: string;
    old_image_url?: string | null;
    new_image_url?: string | null;
    description_updated?: boolean;
    barcode_updated?: boolean;
    name_updated?: boolean;
    image_updated?: boolean;
  }>;
  preview?: Array<{
    sku: string;
    item_name?: string;
    old_price: number | null;
    new_price: number;
    old_description?: string | null;
    new_description?: string;
    old_barcode?: string | null;
    new_barcode?: string;
    old_name?: string | null;
    new_name?: string;
    old_image_url?: string | null;
    new_image_url?: string | null;
    description_updated?: boolean;
    barcode_updated?: boolean;
    name_updated?: boolean;
    image_updated?: boolean;
  }>;
  stock_snapshot?: StockSnapshotItem[];
  message: string;
}

const BulkPriceSyncModal: React.FC<BulkPriceSyncModalProps> = ({ onClose, onSyncComplete }) => {
  const { currentStore } = useStore();
  const { user } = useAuth();
  const [syncing, setSyncing] = useState(false);
  const [stage, setStage] = useState<'idle' | 'preview' | 'syncing' | 'complete'>('idle');
  const [report, setReport] = useState<SyncReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState({ stage: '', current: 0, total: 0, message: '' });
  const [cachedPreviewData, setCachedPreviewData] = useState<SyncReport | null>(null);
  const [showStockComparison, setShowStockComparison] = useState(false);

  const handlePreview = async () => {
    if (!currentStore || !user) return;

    setSyncing(true);
    setError(null);
    // Keep stage as 'idle' while loading so progress bar shows
    setProgress({ stage: 'Fetching POS items...', current: 0, total: 0, message: 'Starting sync preview' });

    try {
      const result = await itemService.compareAndSyncPrices(
        currentStore.id,
        user.id,
        true,
        (stage: string, current: number, total: number, message: string) => {
          setProgress({ stage, current, total, message });
        },
        currentStore.name // Pass store name for outlet-specific pricing
      );

      if (result.error) {
        setError(`Failed to preview prices: ${result.error}`);
        setStage('idle');
      } else if (result.data) {
        setReport(result.data);
        setCachedPreviewData(result.data); // Cache the preview data
        setStage('preview');
      }
    } catch (err) {
      setError(`Failed to preview prices: ${err instanceof Error ? err.message : String(err)}`);
      setStage('idle');
    } finally {
      setSyncing(false);
      setProgress({ stage: '', current: 0, total: 0, message: '' });
    }
  };

  const handleSync = async () => {
    if (!currentStore || !user) return;

    setSyncing(true);
    setError(null);
    setStage('syncing');

    try {
      // If we have cached preview data, apply updates directly without re-fetching
      if (cachedPreviewData && cachedPreviewData.preview && cachedPreviewData.preview.length > 0) {
        console.log('✅ Using cached preview data - skipping re-fetch');
        setProgress({
          stage: 'Applying price updates...',
          current: 0,
          total: cachedPreviewData.preview.length,
          message: `Syncing ${cachedPreviewData.preview.length} prices...`
        });

        // Call RPC directly with cached preview data
        const { data, error } = await supabase.rpc('bulk_sync_prices_from_pos', {
          p_store_id: currentStore.id,
          p_price_updates: cachedPreviewData.preview,
          p_user_id: user.id
        });

        if (error) {
          setError(`Failed to sync prices: ${error.message || String(error)}`);
          setStage('preview');
        } else if (data) {
          // Merge cached metadata with sync result
          setReport({
            ...cachedPreviewData,
            ...data,
            dry_run: false
          });
          setCachedPreviewData(null); // Clear cache after successful sync
          setStage('complete');
          if (onSyncComplete) {
            onSyncComplete();
          }
        }
      } else {
        // No cached data - run full sync (fetch + compare + update)
        console.log('⚠️ No cached data - running full sync');
        setProgress({ stage: 'Fetching POS items...', current: 0, total: 0, message: 'Starting price sync' });

        const result = await itemService.compareAndSyncPrices(
          currentStore.id,
          user.id,
          false,
          (stage: string, current: number, total: number, message: string) => {
            setProgress({ stage, current, total, message });
          },
          currentStore.name // Pass store name for outlet-specific pricing
        );

        if (result.error) {
          setError(`Failed to sync prices: ${result.error}`);
          setStage('preview');
        } else if (result.data) {
          setReport(result.data);
          setStage('complete');
          if (onSyncComplete) {
            onSyncComplete();
          }
        }
      }
    } catch (err) {
      setError(`Failed to sync prices: ${err instanceof Error ? err.message : String(err)}`);
      setStage('preview');
    } finally {
      setSyncing(false);
      setProgress({ stage: '', current: 0, total: 0, message: '' });
    }
  };

  const handleExportToExcel = () => {
    if (!report || !report.stock_snapshot) return;

    const stockSnapshot = report.stock_snapshot || [];
    const differences = stockSnapshot.filter(item =>
      (item.pos_stock || 0) !== (item.stock_room_stock || 0)
    );

    // Prepare data for Excel
    const excelData = differences.map(item => ({
      'SKU': item.sku,
      'Item Name': item.item_name,
      'POS Stock': item.pos_stock || 0,
      'Stock Room Stock': item.stock_room_stock || 0,
      'Difference': (item.pos_stock || 0) - (item.stock_room_stock || 0),
      'Status': (item.pos_stock || 0) > (item.stock_room_stock || 0) ? 'POS Higher' : 'Stock Room Higher',
      'POS Price': `MVR ${(item.pos_price || 0).toFixed(2)}`,
      'Room Price': `MVR ${(item.stock_room_price || 0).toFixed(2)}`
    }));

    // Create worksheet
    const ws = XLSX.utils.json_to_sheet(excelData);

    // Set column widths
    ws['!cols'] = [
      { wch: 15 }, // SKU
      { wch: 35 }, // Item Name
      { wch: 12 }, // POS Stock
      { wch: 18 }, // Stock Room Stock
      { wch: 12 }, // Difference
      { wch: 18 }, // Status
      { wch: 15 }, // POS Price
      { wch: 15 }  // Room Price
    ];

    // Create workbook
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Stock Comparison');

    // Generate filename with date
    const date = new Date().toISOString().split('T')[0];
    const storeName = currentStore?.name.replace(/\s+/g, '_') || 'Store';
    const filename = `Stock_Comparison_${storeName}_${date}.xlsx`;

    // Save file
    XLSX.writeFile(wb, filename);
  };

  const renderIdleState = () => (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-2">How Bulk Item Sync Works:</p>
            <ol className="list-decimal list-inside space-y-1">
              <li>Fetches all items from POS system (Hike API)</li>
              <li>Compares prices, descriptions, barcodes, names & images with Stock Room items by SKU</li>
              <li>Shows preview of all field differences</li>
              <li>Updates prices, descriptions, barcodes, names & images after confirmation</li>
              <li>Saves stock comparison snapshot to database</li>
            </ol>
          </div>
        </div>
      </div>

      {syncing && progress.stage && (
        <div className="bg-gray-50 rounded-lg p-6">
          <div className="text-center mb-4">
            <p className="text-lg font-medium text-gray-900 mb-1">{progress.stage}</p>
            <p className="text-sm text-gray-600">{progress.message}</p>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3 mb-3">
            <div
              className="bg-blue-600 h-3 rounded-full transition-all duration-300"
              style={{ width: `${progress.total > 0 ? (progress.current / progress.total) * 100 : 0}%` }}
            />
          </div>
          {progress.total > 0 && (
            <p className="text-center text-sm text-gray-600">
              {progress.current} / {progress.total}
            </p>
          )}
        </div>
      )}

      <div className="flex justify-end gap-3">
        <button onClick={onClose} className="btn-secondary">
          Cancel
        </button>
        <button onClick={handlePreview} disabled={syncing} className="btn-primary flex items-center gap-2">
          <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
          {syncing ? 'Loading Preview...' : 'Preview Changes'}
        </button>
      </div>
    </div>
  );

  const renderPreviewState = () => {
    if (!report) return null;

    const preview = report.preview || report.changes || [];
    const stockSnapshot = report.stock_snapshot || [];
    const stockDifferences = stockSnapshot.filter(item =>
      (item.pos_stock || 0) !== (item.stock_room_stock || 0)
    ).length;

    return (
      <div className="space-y-6">
        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="text-sm text-gray-600">POS Items</div>
            <div className="text-2xl font-bold text-gray-900">{report.total_pos_items}</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="text-sm text-gray-600">Stock Room Items</div>
            <div className="text-2xl font-bold text-gray-900">{report.total_stockroom_items}</div>
          </div>
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="text-sm text-blue-600">Matched by SKU</div>
            <div className="text-2xl font-bold text-blue-900">{report.matched_by_sku}</div>
          </div>
          <div className="bg-orange-50 rounded-lg p-4">
            <div className="text-sm text-orange-600">Field Differences</div>
            <div className="text-2xl font-bold text-orange-900">{report.price_differences}</div>
          </div>
          <div className="bg-purple-50 rounded-lg p-4">
            <div className="text-sm text-purple-600">Stock Differences</div>
            <div className="text-2xl font-bold text-purple-900">{stockDifferences}</div>
          </div>
        </div>

        {/* Preview Table */}
        {preview.length > 0 ? (
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
              <h4 className="font-semibold text-gray-900">Changes Preview ({preview.length} items)</h4>
            </div>
            <div className="max-h-[500px] overflow-y-auto">
              <div className="divide-y divide-gray-200">
                {preview.map((change, index) => {
                  const oldPrice = change.old_price || 0;
                  const difference = change.new_price - oldPrice;
                  const percentChange = oldPrice > 0 ? ((difference / oldPrice) * 100) : 0;

                  // Only show changes if values differ AND at least one has content (not both empty)
                  const oldDesc = change.old_description || '';
                  const newDesc = change.new_description || '';
                  const hasDescChange = oldDesc !== newDesc && (oldDesc || newDesc);

                  const oldBarcode = change.old_barcode || '';
                  const newBarcode = change.new_barcode || '';
                  const hasBarcodeChange = oldBarcode !== newBarcode && (oldBarcode || newBarcode);

                  const oldName = change.old_name || '';
                  const newName = change.new_name || '';
                  const hasNameChange = oldName !== newName && (oldName || newName);

                  const oldImageUrl = change.old_image_url || '';
                  const newImageUrl = change.new_image_url || '';
                  const hasImageChange = oldImageUrl !== newImageUrl && (oldImageUrl || newImageUrl);

                  return (
                    <div key={index} className="p-4 hover:bg-gray-50">
                      {/* Header Row - Item Name and Price */}
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <div className="text-lg font-semibold text-gray-900 mb-1">
                            {change.item_name || 'Unknown Item'}
                          </div>
                          <span className="font-mono text-sm text-gray-500">SKU: {change.sku}</span>
                        </div>
                        <div className="flex items-center gap-3 text-sm">
                          <span className="text-gray-600">
                            {oldPrice > 0 ? `MVR ${oldPrice.toFixed(2)}` : 'No price'}
                          </span>
                          <span className="text-gray-400">→</span>
                          <span className="font-medium text-blue-600">
                            MVR {change.new_price.toFixed(2)}
                          </span>
                          {oldPrice > 0 && (
                            <span className={`font-medium ${difference >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              ({difference >= 0 ? '+' : ''}{percentChange.toFixed(1)}%)
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Description Change */}
                      {hasDescChange && (
                        <div className="mt-3 bg-blue-50 rounded-lg p-3 border border-blue-100">
                          <div className="flex items-start gap-2">
                            <span className="text-xs font-semibold text-blue-700 uppercase tracking-wide">Description:</span>
                          </div>
                          <div className="mt-2 grid grid-cols-2 gap-3 text-sm">
                            <div>
                              <div className="text-xs text-gray-500 mb-1">Current:</div>
                              <div className="text-gray-700 line-through">
                                {oldDesc || <span className="italic text-gray-400">Empty</span>}
                              </div>
                            </div>
                            <div>
                              <div className="text-xs text-blue-600 mb-1">New:</div>
                              <div className="text-blue-900 font-medium">
                                {newDesc || <span className="italic text-gray-400">Empty</span>}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Barcode Change */}
                      {hasBarcodeChange && (
                        <div className="mt-3 bg-purple-50 rounded-lg p-3 border border-purple-100">
                          <div className="flex items-start gap-2">
                            <span className="text-xs font-semibold text-purple-700 uppercase tracking-wide">Barcode:</span>
                          </div>
                          <div className="mt-2 grid grid-cols-2 gap-3 text-sm">
                            <div>
                              <div className="text-xs text-gray-500 mb-1">Current:</div>
                              <div className="font-mono text-gray-700 line-through">
                                {oldBarcode || <span className="italic text-gray-400">Empty</span>}
                              </div>
                            </div>
                            <div>
                              <div className="text-xs text-purple-600 mb-1">New:</div>
                              <div className="font-mono text-purple-900 font-medium">
                                {newBarcode || <span className="italic text-gray-400">Empty</span>}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Name Change */}
                      {hasNameChange && (
                        <div className="mt-3 bg-green-50 rounded-lg p-3 border border-green-100">
                          <div className="flex items-start gap-2">
                            <span className="text-xs font-semibold text-green-700 uppercase tracking-wide">Item Name:</span>
                          </div>
                          <div className="mt-2 grid grid-cols-2 gap-3 text-sm">
                            <div>
                              <div className="text-xs text-gray-500 mb-1">Current:</div>
                              <div className="text-gray-700 line-through">
                                {oldName || <span className="italic text-gray-400">Empty</span>}
                              </div>
                            </div>
                            <div>
                              <div className="text-xs text-green-600 mb-1">New:</div>
                              <div className="text-green-900 font-medium">
                                {newName || <span className="italic text-gray-400">Empty</span>}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Image Change */}
                      {hasImageChange && (
                        <div className="mt-3 bg-yellow-50 rounded-lg p-3 border border-yellow-100">
                          <div className="flex items-start gap-2">
                            <span className="text-xs font-semibold text-yellow-700 uppercase tracking-wide">Image:</span>
                          </div>
                          <div className="mt-2 grid grid-cols-2 gap-3">
                            <div>
                              <div className="text-xs text-gray-500 mb-2">Current:</div>
                              {oldImageUrl ? (
                                <img
                                  src={oldImageUrl}
                                  alt="Current"
                                  className="w-24 h-24 object-cover rounded border border-gray-300"
                                  onError={(e) => {
                                    (e.target as HTMLImageElement).src = '/images/btlogo.jpg';
                                  }}
                                />
                              ) : (
                                <div className="w-24 h-24 bg-gray-200 rounded border border-gray-300 flex items-center justify-center">
                                  <span className="text-xs text-gray-400">No image</span>
                                </div>
                              )}
                            </div>
                            <div>
                              <div className="text-xs text-yellow-600 mb-2">New:</div>
                              {newImageUrl ? (
                                <img
                                  src={newImageUrl}
                                  alt="New"
                                  className="w-24 h-24 object-cover rounded border border-yellow-300"
                                  onError={(e) => {
                                    (e.target as HTMLImageElement).src = '/images/btlogo.jpg';
                                  }}
                                />
                              ) : (
                                <div className="w-24 h-24 bg-gray-200 rounded border border-gray-300 flex items-center justify-center">
                                  <span className="text-xs text-gray-400">No image</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Tags for quick scan */}
                      <div className="flex gap-2 mt-3">
                        <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded font-medium">
                          Price Updated
                        </span>
                        {hasDescChange && (
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded font-medium">
                            Description Updated
                          </span>
                        )}
                        {hasBarcodeChange && (
                          <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded font-medium">
                            Barcode Updated
                          </span>
                        )}
                        {hasNameChange && (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded font-medium">
                            Name Updated
                          </span>
                        )}
                        {hasImageChange && (
                          <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded font-medium">
                            Image Updated
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-green-50 border border-green-200 rounded-lg p-8 text-center">
            <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-3" />
            <p className="text-lg font-medium text-green-900">All Data Is Up to Date!</p>
            <p className="text-sm text-green-700 mt-1">No differences found between POS and Stock Room.</p>
          </div>
        )}

        {/* Stock Comparison Section */}
        {stockSnapshot.length > 0 && (
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-purple-50 px-4 py-3 border-b border-purple-200 flex items-center justify-between">
              <button
                onClick={() => setShowStockComparison(!showStockComparison)}
                className="flex items-center gap-2 hover:bg-purple-100 transition-colors py-1 px-2 rounded -ml-2"
              >
                <Package className="w-5 h-5 text-purple-600" />
                <h4 className="font-semibold text-purple-900">
                  Stock Comparison ({stockDifferences} differences found)
                </h4>
                {showStockComparison ? (
                  <ChevronUp className="w-5 h-5 text-purple-600 ml-1" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-purple-600 ml-1" />
                )}
              </button>
              {stockDifferences > 0 && (
                <button
                  onClick={handleExportToExcel}
                  className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg transition-colors text-sm font-medium"
                >
                  <Download className="w-4 h-4" />
                  Export to Excel
                </button>
              )}
            </div>

            {showStockComparison && (
              <div className="max-h-96 overflow-y-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Item Name</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">POS Stock</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Room Stock</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Difference</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {stockSnapshot
                      .filter(item => (item.pos_stock || 0) !== (item.stock_room_stock || 0))
                      .sort((a, b) => Math.abs((b.pos_stock || 0) - (b.stock_room_stock || 0)) - Math.abs((a.pos_stock || 0) - (a.stock_room_stock || 0)))
                      .map((item, index) => {
                        const posStock = item.pos_stock || 0;
                        const roomStock = item.stock_room_stock || 0;
                        const difference = posStock - roomStock;
                        const isHigher = difference > 0;

                        return (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm font-mono text-gray-900">{item.sku}</td>
                            <td className="px-4 py-3 text-sm text-gray-900">{item.item_name}</td>
                            <td className="px-4 py-3 text-sm text-right text-gray-900 font-medium">
                              {posStock}
                            </td>
                            <td className="px-4 py-3 text-sm text-right text-gray-900 font-medium">
                              {roomStock}
                            </td>
                            <td className="px-4 py-3 text-sm text-right">
                              <span className={isHigher ? 'text-blue-600' : 'text-orange-600'}>
                                {difference > 0 ? '+' : ''}{difference}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-right">
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                isHigher
                                  ? 'bg-blue-100 text-blue-700'
                                  : 'bg-orange-100 text-orange-700'
                              }`}>
                                {isHigher ? 'POS Higher' : 'Room Higher'}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
                {stockDifferences === 0 && (
                  <div className="p-8 text-center text-gray-500">
                    <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                    <p className="font-medium">All stock levels match!</p>
                    <p className="text-sm mt-1">POS and Stock Room inventory are in sync.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          {preview.length > 0 && (
            <button
              onClick={handleSync}
              disabled={syncing}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Syncing...' : `Apply ${preview.length} Changes`}
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderCompleteState = () => {
    if (!report) return null;

    return (
      <div className="space-y-6">
        {/* Success Header */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
          <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-green-900 mb-2">Sync Complete!</h3>
          <p className="text-green-700">{report.message}</p>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-green-50 rounded-lg p-4 text-center">
            <div className="text-3xl font-bold text-green-900">{report.updated}</div>
            <div className="text-sm text-green-600">Updated</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-4 text-center">
            <div className="text-3xl font-bold text-gray-900">{report.skipped}</div>
            <div className="text-sm text-gray-600">Skipped</div>
          </div>
          <div className="bg-red-50 rounded-lg p-4 text-center">
            <div className="text-3xl font-bold text-red-900">{report.errors?.length || 0}</div>
            <div className="text-sm text-red-600">Errors</div>
          </div>
        </div>

        {/* Changes List */}
        {report.changes && report.changes.length > 0 && (
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
              <h4 className="font-semibold text-gray-900">Updated Items ({report.changes.length})</h4>
            </div>
            <div className="max-h-64 overflow-y-auto">
              <table className="w-full">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Item</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Old</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">New</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {report.changes.map((change, index) => (
                    <tr key={index} className="text-sm">
                      <td className="px-4 py-2">
                        <div className="flex flex-col gap-1">
                          <span className="font-semibold text-gray-900">{change.item_name || 'Unknown Item'}</span>
                          <span className="font-mono text-xs text-gray-500">SKU: {change.sku}</span>
                          <div className="flex gap-1 mt-1">
                            {change.description_updated && (
                              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Desc</span>
                            )}
                            {change.barcode_updated && (
                              <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">Barcode</span>
                            )}
                            {change.name_updated && (
                              <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Name</span>
                            )}
                            {change.image_updated && (
                              <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded">Image</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-2 text-right text-gray-600">
                        {change.old_price ? `MVR ${change.old_price.toFixed(2)}` : '—'}
                      </td>
                      <td className="px-4 py-2 text-right text-blue-600 font-medium">
                        MVR {change.new_price.toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Errors */}
        {report.errors && report.errors.length > 0 && (
          <div className="border border-red-200 rounded-lg overflow-hidden">
            <div className="bg-red-50 px-4 py-3 border-b border-red-200">
              <h4 className="font-semibold text-red-900">Errors ({report.errors.length})</h4>
            </div>
            <div className="max-h-48 overflow-y-auto">
              <ul className="divide-y divide-red-200">
                {report.errors.map((err, index) => (
                  <li key={index} className="px-4 py-3 text-sm">
                    <span className="font-mono text-gray-900">{err.sku}</span>
                    <span className="text-red-600 ml-2">{err.error}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Close Button */}
        <div className="flex justify-end">
          <button onClick={onClose} className="btn-primary">
            Done
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Bulk Item Sync</h2>
            <p className="text-sm text-gray-500">
              {currentStore ? `Syncing items for ${currentStore.name}` : 'Select a store first'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={syncing && stage === 'syncing'}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-red-600" />
                <span className="text-sm text-red-700">{error}</span>
              </div>
            </div>
          )}

          {stage === 'idle' && renderIdleState()}
          {stage === 'preview' && renderPreviewState()}
          {stage === 'syncing' && (
            <div className="flex flex-col items-center justify-center py-12">
              {progress.stage && (
                <div className="w-full max-w-md mb-8">
                  <div className="text-center mb-4">
                    <p className="text-lg font-medium text-gray-900 mb-1">{progress.stage}</p>
                    <p className="text-sm text-gray-600">{progress.message}</p>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3 mb-3">
                    <div
                      className="bg-blue-600 h-3 rounded-full transition-all duration-300"
                      style={{ width: `${progress.total > 0 ? (progress.current / progress.total) * 100 : 0}%` }}
                    />
                  </div>
                  {progress.total > 0 && (
                    <p className="text-center text-sm text-gray-600">
                      {progress.current} / {progress.total}
                    </p>
                  )}
                </div>
              )}
              <LoadingSpinner />
              <p className="mt-4 text-gray-600">Syncing prices...</p>
            </div>
          )}
          {stage === 'complete' && renderCompleteState()}
        </div>
      </div>
    </div>
  );
};

export default BulkPriceSyncModal;
